import React from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Stack,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Divider,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import trustBudMark from "../assets/logo/trustbud-mark.png";
import SeoHead from "../seo/SeoHead";
import { SEO_LANDING_PAGES } from "./SeoLandingPage";

const colors = {
  navy900: "#0c1a2e",
  navy800: "#102544",
  navy700: "#17345d",
  teal: "#2e8b76",
  green: "#2d8a5f",
  text: "#102033",
  secondary: "#39516d",
  muted: "#70839a",
  border: "#d7e2ee",
  panel: "#ffffff",
  canvas: "#f3f6fa",
};

const reportStates = [
  ["Done", "The full report is in."],
  ["Reset", "The full thing slipped. You stayed connected."],
  ["No report", "Nothing was submitted by the deadline."],
];

const proofRows = [
  "Today completion",
  "Private note",
  "Apple Health proof",
  "Partner acknowledgment",
];

const analyticsRows = [
  ["Plan Health", "What to keep, what to change, and what TrustBud will watch."],
  ["Weekly Review", "What worked, what slipped, and one adjustment for next week."],
  ["Analytics", "Report days, resets, proof days, trends, timing, and goal progress."],
];

const homeJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "TrustBud",
  applicationCategory: "LifestyleApplication",
  operatingSystem: "iOS, Web",
  url: "https://trustbud.app",
  description:
    "TrustBud helps people turn meaningful goals into daily action, visible accountability, proof when it matters, and quick recovery after messy days.",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
  },
};

function LogoLockup() {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Box
        component="img"
        src={trustBudMark}
        alt="TrustBud"
        sx={{ width: 34, height: 34, flexShrink: 0 }}
      />
      <Typography sx={{ color: colors.text, fontSize: "1.15rem", fontWeight: 900, letterSpacing: 0 }}>
        TrustBud
      </Typography>
    </Stack>
  );
}

function PrimaryCtas({ dark = false }) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
      <Button
        component={RouterLink}
        to="/register"
        variant="contained"
        size="large"
        sx={{
          bgcolor: dark ? "#ffffff" : colors.navy800,
          color: dark ? colors.navy900 : "#ffffff",
          "&:hover": { bgcolor: dark ? "#e8eef5" : colors.navy900 },
        }}
      >
        Start your first habit
      </Button>
      <Button
        component="a"
        href="#how-it-works"
        variant="outlined"
        size="large"
        sx={{
          color: dark ? "#ffffff" : colors.navy700,
          borderColor: dark ? "rgba(255,255,255,0.32)" : colors.border,
          bgcolor: dark ? "rgba(255,255,255,0.04)" : "#ffffff",
          "&:hover": {
            borderColor: dark ? "rgba(255,255,255,0.54)" : colors.navy700,
            bgcolor: dark ? "rgba(255,255,255,0.08)" : "#ffffff",
          },
        }}
      >
        See how it works
      </Button>
    </Stack>
  );
}

function SectionHeading({ eyebrow, title, body, dark = false }) {
  return (
    <Stack spacing={1.4} sx={{ maxWidth: 720 }}>
      <Typography
        variant="overline"
        sx={{
          color: dark ? "#8fd2c1" : colors.teal,
          fontWeight: 900,
          letterSpacing: 0,
          fontSize: "0.8rem",
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        variant="h2"
        sx={{
          color: dark ? "#ffffff" : colors.text,
          fontWeight: 900,
          letterSpacing: 0,
          lineHeight: 1.05,
          fontSize: { xs: "2rem", md: "2.55rem" },
        }}
      >
        {title}
      </Typography>
      {body ? (
        <Typography sx={{ color: dark ? "rgba(255,255,255,0.74)" : colors.secondary, lineHeight: 1.65 }}>
          {body}
        </Typography>
      ) : null}
    </Stack>
  );
}

function ProductPreview() {
  const habits = [
    ["Eat under 1800 calories", "Done", true],
    ["Do a Duolingo session", "Done", true],
    ["Lift twice a week", "Open", false],
    ["Write in journal", "Can wait", false],
  ];

  return (
    <Box
      sx={{
        bgcolor: "#f8fbff",
        border: "1px solid rgba(255,255,255,0.16)",
        boxShadow: "0 18px 42px rgba(3, 10, 24, 0.28)",
        borderRadius: "8px",
        p: 2,
        maxWidth: 430,
        width: "100%",
      }}
    >
      <Stack spacing={1.5}>
        <Box sx={{ bgcolor: colors.navy900, color: "#ffffff", p: 2, borderRadius: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: "1.2rem" }}>Today</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.9rem" }}>
                2 of 4 habits done
              </Typography>
            </Box>
            <Box
              sx={{
                width: 74,
                height: 74,
                borderRadius: "50%",
                border: "7px solid rgba(143,210,193,0.25)",
                borderTopColor: "#36c983",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Typography sx={{ color: "#ffffff", fontWeight: 900 }}>11d</Typography>
            </Box>
          </Stack>
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2, bgcolor: "#36c983", color: colors.navy900, "&:hover": { bgcolor: "#5dd79f" } }}
          >
            Do next habit
          </Button>
        </Box>

        <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${colors.border}`, borderRadius: 1 }}>
          <Stack divider={<Divider />}>
            {habits.map(([title, state, done]) => (
              <Stack key={title} direction="row" alignItems="center" spacing={1.2} sx={{ p: 1.35 }}>
                {done ? (
                  <CheckCircleRoundedIcon sx={{ color: colors.green }} />
                ) : (
                  <RadioButtonUncheckedRoundedIcon sx={{ color: state === "Can wait" ? colors.muted : "#5b4ee6" }} />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ color: colors.text, fontWeight: 850, fontSize: "0.95rem" }}>{title}</Typography>
                  <Typography sx={{ color: colors.muted, fontSize: "0.82rem" }}>{state}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

function AccountabilityBoard() {
  const rows = [
    ["You", "Done", colors.green],
    ["Carlos", "Done", colors.green],
    ["Ana", "Reset", "#c47a22"],
    ["Terry", "Open", colors.muted],
  ];

  return (
    <Card elevation={0} sx={{ borderColor: "rgba(255,255,255,0.16)", bgcolor: "rgba(255,255,255,0.05)" }}>
      <CardContent sx={{ p: 2.25 }}>
        <Stack spacing={1.5}>
          <Typography sx={{ color: "#ffffff", fontWeight: 900 }}>Today’s Board</Typography>
          {rows.map(([name, state, color]) => (
            <Stack key={name} direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ color: "rgba(255,255,255,0.78)" }}>{name}</Typography>
              <Chip
                size="small"
                label={state}
                sx={{
                  bgcolor: "rgba(255,255,255,0.08)",
                  color,
                  border: "1px solid rgba(255,255,255,0.12)",
                  fontWeight: 800,
                }}
              />
            </Stack>
          ))}
          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Typography sx={{ color: "#8fd2c1", fontWeight: 850 }}>2 done · 1 reset · 1 open</Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: `1px solid ${colors.border}`,
        bgcolor: colors.panel,
        boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack spacing={1.2}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1,
              bgcolor: "#eef6f2",
              color: colors.teal,
              display: "grid",
              placeItems: "center",
            }}
          >
            {icon}
          </Box>
          <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: "1.05rem" }}>{title}</Typography>
          <Typography sx={{ color: colors.secondary, lineHeight: 1.6 }}>{body}</Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function MarketingHomePage() {
  const year = new Date().getFullYear();

  return (
    <Box sx={{ bgcolor: colors.canvas, color: colors.text }}>
      <SeoHead
        title="TrustBud | Habit Accountability, Daily Reports, and Proof"
        description="TrustBud helps you turn meaningful goals into daily action, visible Daily Reports, proof when it matters, and quick recovery after messy days."
        path="/"
        jsonLd={homeJsonLd}
      />
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(18px)",
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Toolbar sx={{ minHeight: 68, justifyContent: "space-between", gap: 2, px: { xs: 1.5, md: 3 } }}>
          <LogoLockup />
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1.25 }} alignItems="center">
            <Button component="a" href="#how-it-works" sx={{ color: colors.secondary, display: { xs: "none", md: "inline-flex" } }}>
              How it works
            </Button>
            <Button component={RouterLink} to="/login" variant="text" size="small" sx={{ whiteSpace: "nowrap" }}>
              Sign in
            </Button>
            <Button component={RouterLink} to="/register" variant="contained" size="small" sx={{ whiteSpace: "nowrap" }}>
              Start
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          background: `linear-gradient(180deg, #ffffff 0%, ${colors.canvas} 100%)`,
        }}
      >
        <Box sx={{ bgcolor: colors.navy900, color: "#ffffff" }}>
          <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
            <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
              <Grid item xs={12} md={6.6}>
                <Stack spacing={3}>
                  <Chip
                    label="Daily action · accountability · recovery"
                    sx={{
                      width: "fit-content",
                      maxWidth: "100%",
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "#ffffff",
                      border: "1px solid rgba(255,255,255,0.14)",
                      fontWeight: 800,
                      "& .MuiChip-label": {
                        overflow: "visible",
                        textOverflow: "clip",
                        whiteSpace: "normal",
                        lineHeight: 1.25,
                        px: { xs: 1.25, sm: 1.5 },
                      },
                    }}
                  />
                  <Stack spacing={1.8}>
                    <Typography
                      variant="h1"
                      sx={{
                        color: "#ffffff",
                        fontWeight: 950,
                        letterSpacing: 0,
                        lineHeight: 1,
                        fontSize: { xs: "2.65rem", sm: "3.55rem", md: "4.55rem" },
                        maxWidth: 720,
                      }}
                    >
                      Follow through when motivation drops.
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.65, fontSize: "1.12rem", maxWidth: 650 }}>
                      TrustBud turns meaningful goals into daily execution, visible Daily Reports, proof when it matters, and a clean way back after messy days.
                    </Typography>
                  </Stack>
                  <PrimaryCtas dark />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2.2}>
                    {["Done", "Reset", "No report visible"].map((item) => (
                      <Stack key={item} direction="row" spacing={0.8} alignItems="center">
                        <CheckCircleRoundedIcon sx={{ color: "#8fd2c1", fontSize: 18 }} />
                        <Typography sx={{ color: "rgba(255,255,255,0.72)", fontWeight: 760 }}>{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} md={5.4}>
                <ProductPreview />
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FeatureCard
                icon={<TimerOutlinedIcon />}
                title="Today stays execution-first"
                body="The app keeps the next action visible and keeps goals from sprawling into another dashboard."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FeatureCard
                icon={<Groups2RoundedIcon />}
                title="Accountability is visible"
                body="One person or group can see whether you reported, reset, or went quiet. That changes avoidance."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FeatureCard
                icon={<InsightsRoundedIcon />}
                title="Insights stay grounded"
                body="Analytics, Plan Health, and Weekly Review focus on real behavior and one useful adjustment."
              />
            </Grid>
          </Grid>
        </Container>

        <Box sx={{ bgcolor: "#ffffff", borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}` }}>
          <Container maxWidth="lg" sx={{ py: { xs: 5, md: 6.5 } }}>
            <Grid container spacing={{ xs: 3, md: 5 }} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <SectionHeading
                  eyebrow="Explore TrustBud"
                  title="Built for the moments where follow-through usually slips."
                  body="Start with the page that matches the kind of accountability you need."
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={1.5}>
                  {SEO_LANDING_PAGES.slice(0, 6).map((page) => (
                    <Grid item xs={12} sm={6} key={page.path}>
                      <Button
                        component={RouterLink}
                        to={page.path}
                        variant="outlined"
                        fullWidth
                        sx={{
                          justifyContent: "space-between",
                          bgcolor: "#ffffff",
                          color: colors.navy700,
                          borderColor: colors.border,
                          minHeight: 58,
                          textTransform: "none",
                          fontWeight: 850,
                          px: 2,
                        }}
                      >
                        {page.eyebrow}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box id="how-it-works" sx={{ bgcolor: "#ffffff", borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}` }}>
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
            <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
              <Grid item xs={12} md={5}>
                <SectionHeading
                  eyebrow="How it works"
                  title="Every day ends with a clear report."
                  body="TrustBud gives every goal a follow-through loop: choose what matters, do one concrete action, report what happened, and return quickly when the day goes sideways."
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <Grid container spacing={1.5}>
                  {reportStates.map(([title, body]) => (
                    <Grid item xs={12} sm={4} key={title}>
                      <Card
                        elevation={0}
                        sx={{
                          height: "100%",
                          bgcolor: title === "Reset" ? "#f5fbf7" : "#ffffff",
                          border: `1px solid ${title === "Reset" ? "#cfe5d7" : colors.border}`,
                          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.035)",
                        }}
                      >
                        <CardContent sx={{ p: 2.2 }}>
                          <Typography sx={{ color: colors.text, fontWeight: 900 }}>{title}</Typography>
                          <Typography sx={{ color: colors.secondary, lineHeight: 1.55, mt: 0.7 }}>{body}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box sx={{ bgcolor: colors.navy900, color: "#ffffff" }}>
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
            <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <SectionHeading
                  eyebrow="Accountability"
                  title="Stay visible to the people who matter."
                  body="Daily Reports make follow-through visible with privacy and control. Your TrustBud sees the report state, the proof you choose to share, and the moments that need a response."
                  dark
                />
                <Stack spacing={1.2} sx={{ mt: 3 }}>
                  {["One-on-one or group Accountability", "Proof optional or required", "Privacy controls for sensitive habits"].map((item) => (
                    <Stack key={item} direction="row" spacing={1} alignItems="center">
                      <ShieldOutlinedIcon sx={{ color: "#8fd2c1", fontSize: 20 }} />
                      <Typography sx={{ color: "rgba(255,255,255,0.78)", fontWeight: 760 }}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <AccountabilityBoard />
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
            <Grid item xs={12} md={5.2}>
              <SectionHeading
                eyebrow="Proof and privacy"
                title="Proof makes accountability stronger when it is needed."
                body="Use proof for goals that need more credibility, while keeping sensitive details private by default."
              />
            </Grid>
            <Grid item xs={12} md={6.8}>
              <Card elevation={0} sx={{ bgcolor: "#ffffff", borderColor: colors.border }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Grid container spacing={1.5}>
                    {proofRows.map((item) => (
                      <Grid item xs={12} sm={6} key={item}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            p: 1.25,
                            bgcolor: "#f8fafc",
                            border: `1px solid ${colors.border}`,
                            borderRadius: "8px",
                          }}
                        >
                          <CheckCircleRoundedIcon sx={{ color: colors.green, fontSize: 20 }} />
                          <Typography sx={{ color: colors.text, fontWeight: 820 }}>{item}</Typography>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                  <Typography sx={{ color: colors.muted, mt: 2, lineHeight: 1.6 }}>
                    Other people see safe report status by default. Proof details stay private unless the user chooses to show them.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        <Box sx={{ bgcolor: "#ffffff", borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}` }}>
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
            <Grid container spacing={{ xs: 4, md: 7 }}>
              <Grid item xs={12} md={5}>
                <SectionHeading
                  eyebrow="Analytics and Plan Health"
                  title="TrustBud turns data into better decisions."
                  body="Paid value comes from clearer analytics, grounded weekly reviews, and plan adjustments that make follow-through easier."
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <Stack spacing={1.5}>
                  {analyticsRows.map(([title, body]) => (
                    <Card
                      key={title}
                      elevation={0}
                      sx={{
                        bgcolor: "#f8fafc",
                        border: `1px solid ${colors.border}`,
                        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.035)",
                      }}
                    >
                      <CardContent sx={{ p: 2.2 }}>
                        <Stack direction="row" spacing={1.4} alignItems="flex-start">
                          <InsightsRoundedIcon sx={{ color: colors.teal, mt: 0.2 }} />
                          <Box>
                            <Typography sx={{ color: colors.text, fontWeight: 900 }}>{title}</Typography>
                            <Typography sx={{ color: colors.secondary, lineHeight: 1.55, mt: 0.4 }}>{body}</Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Card elevation={0} sx={{ bgcolor: colors.navy900, color: "#ffffff", borderColor: colors.navy900 }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={7.5}>
                  <Typography sx={{ color: "#ffffff", fontSize: { xs: "1.9rem", md: "2.45rem" }, lineHeight: 1.08, fontWeight: 950 }}>
                    Build the system that keeps you coming back.
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.74)", mt: 1.3, lineHeight: 1.65 }}>
                    Start with one meaningful goal. Turn it into action for today. Add accountability when staying visible would change the outcome.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4.5}>
                  <PrimaryCtas dark />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Box>

      <Divider />
      <Container maxWidth="lg" sx={{ py: 3.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
          <Typography variant="body2" sx={{ color: colors.muted }}>
            © {year} TrustBud
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button component="a" href="https://trustbud.app/privacy" target="_blank" rel="noreferrer">
              Privacy Policy
            </Button>
            <Button component="a" href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noreferrer">
              Terms of Use
            </Button>
            <Button component="a" href="mailto:support@trustbud.app">
              Contact Support
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
